import { getUtilAuthToken } from '@home/services/authPath';

interface IBuildHTMLReportPreviewUrlOptions {
  reportFileKey: string;
  menuId?: string;
  pageType?: string;
  getObjectUrl: (params: { key: string }) => Promise<{ url: string }>;
}

interface IReportOriginalFilePaths {
  filePathCN: string;
  filePathEN: string;
}

const DEFAULT_REPORT_PREVIEW_MENU_ID = '';
const DEFAULT_REPORT_PREVIEW_PAGE_TYPE = 'auth_path';
const DEFAULT_REPORT_PREVIEW_RES_ID = 'intelligence';
const REPORT_ORIGINAL_FILE_PATH_SEPARATOR = '; ';

export const parseReportOriginalFilePaths = (attachment?: string): IReportOriginalFilePaths => {
  const [filePathCN = '', ...filePathENSegments] = (attachment || '').split(';');
  const filePathEN = filePathENSegments.join(';').replace(/^ /, '');
  return { filePathCN, filePathEN };
};

export const serializeReportOriginalFilePaths = (paths: IReportOriginalFilePaths): string => {
  const filePathCN = paths?.filePathCN || '';
  const filePathEN = paths?.filePathEN || '';
  if (!filePathCN && !filePathEN) {
    return '';
  }
  return `${filePathCN}${REPORT_ORIGINAL_FILE_PATH_SEPARATOR}${filePathEN}`;
};

const getPureFilePath = (path?: string): string => {
  return (path || '').split(/[?#]/)[0].toLowerCase();
};

export const getIsHTMLReportPath = (path?: string): boolean => {
  const purePath = getPureFilePath(path);
  return purePath.endsWith('.html') || purePath.endsWith('.htm');
};

export const getIsZipReportPath = (path?: string): boolean => {
  return getPureFilePath(path).endsWith('.zip');
};

export const buildAuthTokenUrl = (url: string, token: string): string => {
  const connector = url.includes('?') ? '&' : '?';
  return `${url}${connector}token=${encodeURIComponent(token)}`;
};

const getHTMLReportResourceUrl = async (
  reportFileKey: string,
  getObjectUrl: IBuildHTMLReportPreviewUrlOptions['getObjectUrl'],
): Promise<string> => {
  if (reportFileKey.startsWith('/') || /^https?:\/\//i.test(reportFileKey)) {
    return reportFileKey;
  }
  const { url } = await getObjectUrl({ key: reportFileKey });
  return url;
};

export const buildHTMLReportPreviewUrl = async (options: IBuildHTMLReportPreviewUrlOptions): Promise<string> => {
  const {
    reportFileKey,
    menuId = DEFAULT_REPORT_PREVIEW_MENU_ID,
    pageType = DEFAULT_REPORT_PREVIEW_PAGE_TYPE,
    getObjectUrl,
  } = options;
  const url = await getHTMLReportResourceUrl(reportFileKey, getObjectUrl);
  const response = await getUtilAuthToken({
    res_id: DEFAULT_REPORT_PREVIEW_RES_ID,
    menu_id: menuId,
    page_type: pageType,
    page_value: url,
  });
  const token = response?.data?.token || '';
  if (!token) {
    throw new Error('Failed to get html report auth token.');
  }
  return buildAuthTokenUrl(url, token);
};
